// Legacy, unowned storage is deliberately never imported into an account.
// The current browser user cannot prove who originally created those records.
import { remoteAccountDocument, saveAccountDocument } from './accountPersistence';
export type StudyScope = { ownerId: string | null; revision: number; ready: boolean; guestId?: string };
let scope: StudyScope = { ownerId: null, revision: 0, ready: false };

export function getStudyScope(): StudyScope { return scope; }
export function isStudyScopeCurrent(value: StudyScope): boolean { return value === scope && scope.ready; }

export function setStudyOwner(ownerId: string | null) {
  if (scope.ready && scope.ownerId === ownerId) return;
  const previous = scope;
  const isFlowKey = (key: string) => key.startsWith('scan_') || key.startsWith('ai_request_') ||
    key === 'lavyn_generation_job' || key === 'history_scroll_pos';
  // Preserve verified-account recovery state without leaving it in shared keys.
  if (previous.ready && previous.ownerId) {
    try {
      const prefix = studyStorageKey('flow:', previous);
      const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i)).filter((key): key is string => Boolean(key));
      keys.filter(key => key.startsWith(prefix)).forEach(key => sessionStorage.removeItem(key));
      keys.filter(isFlowKey).forEach(key => {
        const value = sessionStorage.getItem(key);
        if (value !== null) sessionStorage.setItem(studyStorageKey(`flow:${key}`, previous), value);
      });
    } catch { /* A storage failure must not prevent sign-out. */ }
  }
  // Mint before touching storage: a storage failure must never fall back to a
  // reusable numeric guest namespace after a reload.
  scope = { ownerId, revision: previous.revision + 1, ready: true,
    ...(!ownerId ? { guestId: crypto.randomUUID() } : {}) };
  try {
    const owner = ownerId || 'guest';
    const switching = sessionStorage.getItem('lavyn_scan_owner_v2') !== owner ||
      (previous.ready && previous.ownerId !== ownerId);
    if (switching) {
      const remove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (isFlowKey(key) || key.startsWith('lavyn_private_guest:'))) remove.push(key);
      }
      remove.forEach(key => sessionStorage.removeItem(key));
    }
    sessionStorage.setItem('lavyn_scan_owner_v2', owner);
    if (ownerId && switching) {
      const prefix = studyStorageKey('flow:', scope);
      const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i)).filter((key): key is string => Boolean(key));
      keys.filter(key => key.startsWith(prefix)).forEach(key => {
        const value = sessionStorage.getItem(key);
        if (value !== null) sessionStorage.setItem(key.slice(prefix.length), value);
        sessionStorage.removeItem(key);
      });
    }
    if (!ownerId) {
      const saved = sessionStorage.getItem('lavyn_private_guest:identity');
      if (!switching && saved && /^[0-9a-f-]{36}$/i.test(saved)) scope.guestId = saved;
      sessionStorage.setItem('lavyn_private_guest:identity', scope.guestId);
    }
  } catch { /* Storage may be unavailable; identity isolation still applies. */ }
}

export function studyStorageKey(key: string, value = scope): string {
  return value.ownerId
    ? `lavyn_private_v2:${encodeURIComponent(value.ownerId)}:${key}`
    : `lavyn_private_guest:${value.guestId || value.revision}:${key}`;
}

export function accountStore<T>(key: string, empty: () => T) {
  return {
    read(owner = scope): T {
      if (!owner.ready || (!owner.ownerId && !isStudyScopeCurrent(owner))) return empty();
      const remote = remoteAccountDocument(owner.ownerId, key);
      if (remote) return remote.payload as T;
      try {
        const storage = owner.ownerId ? localStorage : sessionStorage;
        const raw = storage.getItem(studyStorageKey(key, owner));
        return raw ? JSON.parse(raw) as T : empty();
      } catch { return empty(); }
    },
    write(value: T, owner = scope) {
      if (!owner.ready || (!owner.ownerId && !isStudyScopeCurrent(owner))) return;
      try {
        const storage = owner.ownerId ? localStorage : sessionStorage;
        storage.setItem(studyStorageKey(key, owner), JSON.stringify(value));
      } catch { /* Keep storage failures contained, as in the existing stores. */ }
      void saveAccountDocument(owner.ownerId, key, value).catch(() => { /* Save warning is displayed by AccountDataSync. */ });
    },
  };
}

export function readGenerationSession(key: string, owner: StudyScope): string | null {
  if (!owner.ownerId && !isStudyScopeCurrent(owner)) return null;
  return sessionStorage.getItem(isStudyScopeCurrent(owner) ? key : studyStorageKey(`flow:${key}`, owner));
}

export function writeGenerationSession(key: string, value: string, owner: StudyScope) {
  if (!owner.ownerId && !isStudyScopeCurrent(owner)) return;
  const target = isStudyScopeCurrent(owner) ? key : studyStorageKey(`flow:${key}`, owner);
  if (!isStudyScopeCurrent(owner) && key === 'lavyn_generation_job') {
    const previous = sessionStorage.getItem(target);
    if (previous) {
      try {
        if (JSON.parse(previous).requestId !== JSON.parse(value).requestId) return;
      } catch { return; }
    }
  }
  sessionStorage.setItem(target, value);
}
